import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import {
    MessageSquare, Send, RefreshCw, Cpu, GitBranch, User, Bot,
    AlertTriangle, Sparkles, Clock, DollarSign, Hash, Trash2, X,
} from "lucide-react";
import { toast } from "sonner";

// Poll knobs — 90s total budget at 800ms intervals
const POLL_INTERVAL_MS = 800;
const POLL_MAX_MS = 90_000;

export default function QaChatPage() {
    const [clients, setClients] = useState([]);
    const [flows, setFlows] = useState([]);
    const [loading, setLoading] = useState(true);
    const [clientId, setClientId] = useState("");
    const [flowId, setFlowId] = useState("");
    const [contextFields, setContextFields] = useState({}); // dynamic non-client_id inputs
    const [messages, setMessages] = useState([]); // [{role, text, meta?, error?}]
    const [draft, setDraft] = useState("");
    const [sending, setSending] = useState(false);
    const scrollRef = useRef(null);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const [clientsRes, flowsRes] = await Promise.all([
                api.get("/admin/clients"),
                api.get("/admin/flows?tag=chat"),
            ]);
            const cs = (clientsRes.data?.items || [])
                .filter((c) => c.status !== "disabled")
                .sort((a, b) => (a.client_name || a.client_id).localeCompare(b.client_name || b.client_id));
            setClients(cs);
            const fs = (flowsRes.data?.items || []).filter((f) => f.status === "active");
            setFlows(fs);
            // Pre-select defaults if any
            if (cs.length && !clientId) setClientId(cs[0].client_id);
            if (fs.length && !flowId) setFlowId(fs[0].id);
        } catch (e) {
            toast.error(formatApiErrorDetail(e?.response?.data?.detail));
        } finally {
            setLoading(false);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => { load(); }, [load]);

    const selectedFlow = useMemo(
        () => flows.find((f) => f.id === flowId) || null,
        [flows, flowId],
    );

    // Resolve the active version's input_schema for dynamic context fields.
    // We rely on the flow doc's own schema first; if missing, the chat is
    // still usable with just `question`.
    const [schemaFields, setSchemaFields] = useState([]);
    useEffect(() => {
        let cancelled = false;
        async function fetchSchema() {
            if (!selectedFlow) { setSchemaFields([]); return; }
            try {
                const { data } = await api.get(`/v1/flows/${selectedFlow.slug}?environment=prod`);
                if (cancelled) return;
                const fields = (data?.input_schema?.fields || [])
                    .filter((f) => f.name !== "client_id" && f.name !== "question")
                    .map((f) => ({ name: f.name, required: !!f.required, description: f.description || "" }));
                setSchemaFields(fields);
                // Reset context inputs to empty values for the new flow.
                setContextFields((prev) => {
                    const next = {};
                    for (const f of fields) next[f.name] = prev[f.name] || "";
                    return next;
                });
            } catch {
                setSchemaFields([]);
            }
        }
        fetchSchema();
        return () => { cancelled = true; };
    }, [selectedFlow]);

    useEffect(() => {
        // Auto-scroll to bottom on new message
        if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }, [messages, sending]);

    const resetConversation = () => setMessages([]);

    const validateBeforeSend = () => {
        if (!clientId) return "Select a client.";
        if (!selectedFlow) return "Select a chat flow.";
        for (const f of schemaFields) {
            if (f.required && !(contextFields[f.name] || "").trim()) {
                return `Field "${f.name}" is required for this chat flow.`;
            }
        }
        if (!draft.trim()) return "Type a question first.";
        return null;
    };

    const sendMessage = async () => {
        const reason = validateBeforeSend();
        if (reason) { toast.error(reason); return; }
        const question = draft.trim();
        setDraft("");
        const userMsg = { role: "user", text: question, ts: new Date().toISOString() };
        setMessages((prev) => [...prev, userMsg]);
        setSending(true);

        const input_payload = { client_id: clientId, question };
        for (const f of schemaFields) {
            const v = (contextFields[f.name] || "").trim();
            if (v) input_payload[f.name] = v;
        }

        try {
            const { data: started } = await api.post(
                `/admin/flows/${selectedFlow.id}/run`,
                { input_payload, client_id: clientId },
            );
            const runId = started.id;
            const result = await pollFlowRun(selectedFlow.id, runId);
            if (result.status !== "completed") {
                throw new Error(
                    `Flow ${result.status || "failed"}: ${result.error_message || result.error || "no detail"}`,
                );
            }
            const out = result.output_payload || {};
            // The protocol-chat flow returns `{ answer: { answer, citations,
            // confidence, from_version, protocol_number, irb_approved_used, ... } }`
            // so we may need to unwrap one extra level. Other chat flows just
            // return a string under `answer` or `response`.
            const rawAnswer = out.answer ?? out.response ?? out;
            const chatObj = (rawAnswer && typeof rawAnswer === "object" && typeof rawAnswer.answer === "string")
                ? rawAnswer : null;
            const text = chatObj ? chatObj.answer
                : (typeof rawAnswer === "string" ? rawAnswer : JSON.stringify(rawAnswer, null, 2));
            setMessages((prev) => [
                ...prev,
                {
                    role: "assistant",
                    text,
                    ts: new Date().toISOString(),
                    citations: chatObj?.citations || [],
                    meta: {
                        runId,
                        cost: result.totals?.cost_usd,
                        latencyMs: result.totals?.latency_ms,
                        tokensIn: result.totals?.tokens_in,
                        tokensOut: result.totals?.tokens_out,
                        versionNo: result.version_no,
                        protocolNumber: chatObj?.protocol_number,
                        fromVersion: chatObj?.from_version,
                        confidence: chatObj?.confidence,
                        irbApprovedUsed: chatObj?.irb_approved_used,
                        sourcesConsulted: chatObj?.sources_consulted,
                        // Cost breakdown — populated by the chat agent.
                        llmCostUsd:           chatObj?._llm_cost_usd,
                        reuseChargeUsd:       chatObj?._reuse_charge_usd,
                        interactionChargeUsd: chatObj?._interaction_charge_usd,
                        totalCostUsd:         chatObj?._total_cost_usd,
                        cached:               chatObj?._cached,
                    },
                },
            ]);
        } catch (e) {
            const msg = formatApiErrorDetail(e?.response?.data?.detail) || e.message || "Request failed";
            setMessages((prev) => [...prev, { role: "assistant", error: msg, ts: new Date().toISOString() }]);
            toast.error(msg);
        } finally {
            setSending(false);
        }
    };

    const pollFlowRun = async (flow_id, run_id) => {
        const started = Date.now();
        while (Date.now() - started < POLL_MAX_MS) {
            try {
                const { data } = await api.get(`/admin/flows/${flow_id}/runs/${run_id}`);
                if (["completed", "failed", "cancelled", "rejected"].includes(data.status)) {
                    return data;
                }
            } catch (e) {
                // 404 means the run hasn't materialised yet — keep polling for a bit.
                if (e?.response?.status && e.response.status >= 500) throw e;
            }
            await new Promise((r) => setTimeout(r, POLL_INTERVAL_MS));
        }
        throw new Error(`Polling timeout after ${POLL_MAX_MS / 1000}s`);
    };

    const onKeyDown = (e) => {
        if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) {
            e.preventDefault();
            sendMessage();
        }
    };

    return (
        <div className="px-8 py-8" data-testid="qa-chat-page">
            <div className="flex items-start justify-between mb-7">
                <div>
                    <h1 className="text-2xl font-display tracking-tight mb-1">Chat</h1>
                    <p className="text-sm text-white/50 max-w-3xl">
                        QA testing point for every published <strong>chat</strong> flow
                        (tag <code className="text-white/70">chat</code>). Pick a client + flow,
                        send a question, and inspect the answer + run metadata. Calls go through
                        <code className="text-white/70 mx-1">/api/admin/flows/{`{id}`}/run</code>
                        so you never need a tenant API key for QA.
                    </p>
                </div>
                <button
                    type="button"
                    onClick={load}
                    data-testid="qa-chat-refresh"
                    className="flex items-center gap-1.5 px-3 py-2 rounded-md border border-white/15 text-xs text-white/70 hover:text-white hover:bg-white/5"
                >
                    <RefreshCw className="w-3.5 h-3.5" strokeWidth={1.5} />Refresh
                </button>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-[340px_1fr] gap-6">
                <div className="space-y-4">
                    <ContextPanel
                        clients={clients}
                        clientId={clientId} setClientId={setClientId}
                        flows={flows}
                        flowId={flowId} setFlowId={setFlowId}
                        selectedFlow={selectedFlow}
                        schemaFields={schemaFields}
                        contextFields={contextFields} setContextFields={setContextFields}
                        loading={loading}
                    />
                    <CapabilityPanel selectedFlow={selectedFlow} />
                </div>

                <ChatPanel
                    scrollRef={scrollRef}
                    messages={messages}
                    sending={sending}
                    draft={draft} setDraft={setDraft}
                    onSend={sendMessage}
                    onReset={resetConversation}
                    onKeyDown={onKeyDown}
                    disabled={!clientId || !selectedFlow}
                />
            </div>
        </div>
    );
}

// ============================================================
//  Left: client + flow picker + dynamic context fields
// ============================================================
function ContextPanel({
    clients, clientId, setClientId, flows, flowId, setFlowId, selectedFlow,
    schemaFields, contextFields, setContextFields, loading,
}) {
    return (
        <div className="rounded-lg border border-white/10 bg-white/[0.02] p-4 space-y-4">
            <div>
                <label className="block text-[11px] uppercase tracking-wider text-white/40 mb-1">Client</label>
                <select
                    value={clientId}
                    onChange={(e) => setClientId(e.target.value)}
                    disabled={loading || !clients.length}
                    data-testid="qa-chat-client-select"
                    className="w-full px-3 py-2 rounded-md bg-black/40 border border-white/15 text-sm text-white/85 focus:outline-none focus:border-white/35 disabled:opacity-40"
                >
                    {!clients.length && <option value="">(no active clients)</option>}
                    {clients.map((c) => (
                        <option key={c.client_id} value={c.client_id}>
                            {(c.client_name || c.client_id)}{c.is_superdomain ? " · superdomain" : ""}
                        </option>
                    ))}
                </select>
            </div>

            <div>
                <label className="block text-[11px] uppercase tracking-wider text-white/40 mb-1">Chat type</label>
                <select
                    value={flowId}
                    onChange={(e) => setFlowId(e.target.value)}
                    disabled={loading || !flows.length}
                    data-testid="qa-chat-flow-select"
                    className="w-full px-3 py-2 rounded-md bg-black/40 border border-white/15 text-sm text-white/85 focus:outline-none focus:border-white/35 disabled:opacity-40"
                >
                    {!flows.length && <option value="">(no published chat flows)</option>}
                    {flows.map((f) => (
                        <option key={f.id} value={f.id}>
                            {f.name}{(f.tags || []).includes("deprecated") ? "" : ` · ${f.slug}`}
                        </option>
                    ))}
                </select>
                {selectedFlow?.description && (
                    <p className="text-[11px] text-white/45 mt-1.5 leading-relaxed">{selectedFlow.description}</p>
                )}
            </div>

            {schemaFields.length > 0 && (
                <div className="space-y-3 pt-2 border-t border-white/10">
                    <div className="text-[10px] uppercase tracking-wider text-white/40">Flow inputs</div>
                    {schemaFields.map((f) => (
                        <div key={f.name}>
                            <label className="flex items-center gap-1 text-[11px] text-white/55 mb-1">
                                <span className="font-mono">{f.name}</span>
                                {f.required && <span className="text-rose-400">*</span>}
                            </label>
                            <input
                                type="text"
                                value={contextFields[f.name] || ""}
                                onChange={(e) => setContextFields({ ...contextFields, [f.name]: e.target.value })}
                                placeholder={f.description || ""}
                                data-testid={`qa-chat-input-${f.name}`}
                                className="w-full px-3 py-1.5 rounded-md bg-black/40 border border-white/15 text-sm text-white/85 focus:outline-none focus:border-white/35"
                            />
                            {f.description && (
                                <p className="text-[10px] text-white/35 mt-0.5">{f.description}</p>
                            )}
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
}

// ============================================================
//  Capability summary card (agent / flow / endpoint info)
// ============================================================
function CapabilityPanel({ selectedFlow }) {
    if (!selectedFlow) return null;
    return (
        <div className="rounded-lg border border-white/10 bg-white/[0.02] p-4 space-y-2 text-[11px]">
            <div className="flex items-center gap-2 text-white/60">
                <GitBranch className="w-3.5 h-3.5" strokeWidth={1.5} />
                <span className="font-mono">{selectedFlow.slug}</span>
                <span className="text-white/40">· v{selectedFlow.active_version_no}</span>
            </div>
            {(selectedFlow.tags || []).length > 0 && (
                <div className="flex flex-wrap gap-1">
                    {selectedFlow.tags.map((t) => (
                        <span key={t} className="px-1.5 py-0.5 rounded-sm bg-white/5 border border-white/10 text-[10px] text-white/55 font-mono">{t}</span>
                    ))}
                </div>
            )}
            <div className="text-white/40 font-mono text-[10px] pt-1 break-all">
                POST /api/v1/flows/{selectedFlow.slug}/prod
            </div>
        </div>
    );
}

// ============================================================
//  Right: chat conversation panel
// ============================================================
function ChatPanel({ scrollRef, messages, sending, draft, setDraft, onSend, onReset, onKeyDown, disabled }) {
    return (
        <div className="rounded-lg border border-white/10 bg-white/[0.02] flex flex-col h-[640px]">
            <div className="px-5 py-3 border-b border-white/10 flex items-center justify-between">
                <div className="flex items-center gap-2">
                    <MessageSquare className="w-4 h-4 text-white/40" strokeWidth={1.5} />
                    <h3 className="text-sm font-medium tracking-tight">Conversation</h3>
                    {messages.length > 0 && (
                        <span className="text-[10px] text-white/35 font-mono">{messages.length} message{messages.length !== 1 ? "s" : ""}</span>
                    )}
                </div>
                {messages.length > 0 && (
                    <button
                        type="button"
                        onClick={onReset}
                        data-testid="qa-chat-reset-conversation"
                        className="flex items-center gap-1 text-[11px] text-white/50 hover:text-white/85"
                    >
                        <Trash2 className="w-3 h-3" strokeWidth={1.5} />Clear
                    </button>
                )}
            </div>

            <div ref={scrollRef} className="flex-1 overflow-y-auto px-5 py-4 space-y-3" data-testid="qa-chat-messages">
                {messages.length === 0 && !sending && <EmptyChatState />}
                {messages.map((m, i) => <ChatBubble key={i} m={m} />)}
                {sending && <TypingIndicator />}
            </div>

            <div className="px-5 py-3 border-t border-white/10">
                <div className="flex gap-2">
                    <textarea
                        value={draft}
                        onChange={(e) => setDraft(e.target.value)}
                        onKeyDown={onKeyDown}
                        rows={2}
                        placeholder={disabled ? "Pick a client + chat flow to start." : "Ask a question… (⌘+Enter to send)"}
                        disabled={disabled || sending}
                        data-testid="qa-chat-input"
                        className="flex-1 px-3 py-2 rounded-md bg-black/40 border border-white/15 text-sm text-white/85 focus:outline-none focus:border-white/35 resize-none disabled:opacity-40"
                    />
                    <button
                        type="button"
                        onClick={onSend}
                        disabled={disabled || sending || !draft.trim()}
                        data-testid="qa-chat-send-btn"
                        className="flex items-center gap-1.5 px-4 rounded-md bg-[#7C3AED] hover:bg-[#6D28D9] disabled:opacity-40 disabled:cursor-not-allowed text-sm font-medium self-stretch"
                    >
                        <Send className="w-3.5 h-3.5" strokeWidth={1.75} />
                        Send
                    </button>
                </div>
            </div>
        </div>
    );
}

function EmptyChatState() {
    return (
        <div className="flex flex-col items-center justify-center h-full text-white/40">
            <Sparkles className="w-8 h-8 mb-3" strokeWidth={1} />
            <p className="text-xs">Send a question to start the conversation.</p>
            <p className="text-[10px] mt-1 opacity-60">Each turn runs the published chat flow and shows cost + latency.</p>
        </div>
    );
}

function TypingIndicator() {
    return (
        <div className="flex items-start gap-3" data-testid="qa-chat-typing">
            <div className="w-7 h-7 rounded-md bg-violet-400/10 border border-violet-400/20 flex items-center justify-center flex-shrink-0">
                <Bot className="w-3.5 h-3.5 text-violet-300" strokeWidth={1.5} />
            </div>
            <div className="px-3 py-2 rounded-md bg-white/[0.04] border border-white/10 text-xs text-white/55 flex items-center gap-1">
                <span className="inline-block w-1.5 h-1.5 rounded-full bg-violet-400 animate-pulse" />
                <span className="inline-block w-1.5 h-1.5 rounded-full bg-violet-400 animate-pulse" style={{ animationDelay: "150ms" }} />
                <span className="inline-block w-1.5 h-1.5 rounded-full bg-violet-400 animate-pulse" style={{ animationDelay: "300ms" }} />
                <span className="ml-2">thinking…</span>
            </div>
        </div>
    );
}

function ChatBubble({ m }) {
    const isUser = m.role === "user";
    const Icon = isUser ? User : Bot;
    const iconTone = isUser ? "bg-sky-400/10 border-sky-400/20 text-sky-300" : "bg-violet-400/10 border-violet-400/20 text-violet-300";
    return (
        <div className={`flex items-start gap-3 ${isUser ? "" : ""}`} data-testid={`qa-chat-bubble-${m.role}`}>
            <div className={`w-7 h-7 rounded-md border flex items-center justify-center flex-shrink-0 ${iconTone}`}>
                <Icon className="w-3.5 h-3.5" strokeWidth={1.5} />
            </div>
            <div className="flex-1 min-w-0">
                {m.error ? (
                    <div className="px-3 py-2 rounded-md border border-rose-400/30 bg-rose-400/5 text-rose-200 text-xs flex gap-2">
                        <AlertTriangle className="w-3.5 h-3.5 flex-shrink-0 mt-0.5" strokeWidth={1.5} />
                        <span className="font-mono whitespace-pre-wrap break-words">{m.error}</span>
                    </div>
                ) : (
                    <>
                        {m.meta?.irbApprovedUsed === false && (
                            <div className="mb-1.5 px-2 py-1 rounded-md border border-amber-400/30 bg-amber-400/5 text-amber-200 text-[11px] flex items-center gap-1.5"
                                 data-testid="qa-chat-irb-disclosure">
                                <AlertTriangle className="w-3 h-3" strokeWidth={1.5} />
                                Sourced from a <strong>non-IRB-approved</strong> version. Treat as informational only.
                            </div>
                        )}
                        <div className="px-3 py-2 rounded-md bg-white/[0.04] border border-white/10 text-sm text-white/85 whitespace-pre-wrap break-words">
                            {m.text}
                        </div>
                        {(m.citations || []).length > 0 && (
                            <div className="mt-1.5 space-y-1" data-testid="qa-chat-citations">
                                {m.citations.slice(0, 4).map((c, i) => (
                                    <div key={i} className="text-[11px] text-white/55 pl-3 border-l-2 border-violet-400/30">
                                        <span className="text-violet-300/80 font-mono mr-1.5">{c.section || "—"}</span>
                                        <span className="italic">“{c.quote}”</span>
                                    </div>
                                ))}
                            </div>
                        )}
                    </>
                )}
                {m.meta && <ChatMeta meta={m.meta} />}
            </div>
        </div>
    );
}

function ChatMeta({ meta }) {
    // Prefer the agent-reported total cost; fall back to the flow-totals cost.
    const total = meta.totalCostUsd ?? meta.cost;
    const fmt = (n) => (typeof n === "number" ? n.toFixed(5) : null);
    return (
        <div className="flex flex-wrap gap-2 mt-1.5 text-[10px] text-white/40 font-mono">
            {meta.protocolNumber && (
                <span className="text-violet-300/80">{meta.protocolNumber}{meta.fromVersion ? ` · v${meta.fromVersion}` : ""}</span>
            )}
            {meta.confidence && (
                <span className={meta.confidence === "high" ? "text-emerald-300/80"
                    : meta.confidence === "medium" ? "text-amber-300/80" : "text-rose-300/80"}>
                    confidence: {meta.confidence}
                </span>
            )}
            {(meta.sourcesConsulted || []).length > 0 && (
                <span className="text-sky-300/80">sources: {(meta.sourcesConsulted || []).join(" + ")}</span>
            )}
            {meta.cached === true && (
                <span className="px-1.5 py-0.5 rounded-sm bg-violet-400/10 border border-violet-400/20 text-violet-300/85"
                    data-testid="qa-chat-cached-badge">cached</span>
            )}
            {meta.latencyMs != null && (
                <span className="flex items-center gap-1"><Clock className="w-3 h-3" strokeWidth={1.5} />{(meta.latencyMs / 1000).toFixed(2)}s</span>
            )}
            {/* Cost breakdown — shows total + tooltip with the three components. */}
            {(total != null) && (
                <span className="flex items-center gap-1 text-amber-300/85"
                    data-testid="qa-chat-cost"
                    title={[
                        meta.llmCostUsd != null ? `LLM: $${fmt(meta.llmCostUsd)}` : null,
                        meta.reuseChargeUsd != null ? `Datalake reuse: $${fmt(meta.reuseChargeUsd)}` : null,
                        meta.interactionChargeUsd != null ? `Chat interaction: $${fmt(meta.interactionChargeUsd)}` : null,
                    ].filter(Boolean).join(" + ") || `Total: $${fmt(total)}`}>
                    <DollarSign className="w-3 h-3" strokeWidth={1.5} />
                    {fmt(total)}
                </span>
            )}
            {(meta.tokensIn != null || meta.tokensOut != null) && (
                <span className="flex items-center gap-1"><Cpu className="w-3 h-3" strokeWidth={1.5} />in {meta.tokensIn ?? 0} · out {meta.tokensOut ?? 0}</span>
            )}
            {meta.runId && (
                <span className="flex items-center gap-1 opacity-60"><Hash className="w-3 h-3" strokeWidth={1.5} />{meta.runId.slice(0, 8)}</span>
            )}
            {/* Cost breakdown row when ANY of the three sub-costs is set */}
            {(meta.llmCostUsd != null || meta.reuseChargeUsd != null || meta.interactionChargeUsd != null) && (
                <div className="basis-full flex flex-wrap gap-2 text-[10px] text-white/35"
                    data-testid="qa-chat-cost-breakdown">
                    {meta.llmCostUsd != null && <span>llm ${fmt(meta.llmCostUsd)}</span>}
                    {meta.reuseChargeUsd != null && <span>+ reuse ${fmt(meta.reuseChargeUsd)}</span>}
                    {meta.interactionChargeUsd != null && <span>+ interaction ${fmt(meta.interactionChargeUsd)}</span>}
                </div>
            )}
        </div>
    );
}
